import React, { useState, useEffect, useRef } from 'react';
import { Check, AlertCircle, Star, Zap } from 'lucide-react';
import { Spinner } from '../../design-system';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { format } from 'date-fns';
import PostHogService, { BillingEvent } from '../../posthog';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { startCheckout } from '../../lib/paddle';
import { isUnlimitedQuota } from '../../utils/quota';

interface SubscriptionTier {
  id: string;
  name: string;
  display_name: string;
  price: number;
  credits: number;
  description?: string;
  features: string[];
  is_active: boolean;
  metadata?: {
    requires_sales_contact?: boolean;
    signup_enabled?: boolean;
    quota_prs_per_month?: number;
    quota_sast_scans_per_month?: number;
    quota_deep_scans_per_month?: number;
    topup_price_pr?: number;
    topup_price_sast_scan?: number;
    topup_price_deep_scan?: number;
  };
}

interface CurrentSubscription {
  id: string;
  tier_name: string;
  status: string;
  monthly_price: number;
  current_period_end: string;
  next_billing_date: string;
  cancelled_at?: string | null;
  provider?: string | null;
  payment_past_due?: boolean;
}

interface QuotaBalance {
  period_start: string;
  period_end: string;
  plan_prs: number;
  topup_prs: number;
  used_prs: number;
  remaining_prs: number;
  plan_sast_scans: number;
  topup_sast_scans: number;
  used_sast_scans: number;
  remaining_sast_scans: number;
  plan_deep_scans: number;
  topup_deep_scans: number;
  used_deep_scans: number;
  remaining_deep_scans: number;
}

export const SubscriptionSection: React.FC = () => {
  const api = useAuthenticatedApi();
  const { currentOrganization } = useOrganization();
  const { accessType } = useSubscriptionAccess();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [quotaBalance, setQuotaBalance] = useState<QuotaBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [uncancelling, setUncancelling] = useState(false);
  const [pendingTier, setPendingTier] = useState<string | null>(null);
  const [planChangeTier, setPlanChangeTier] = useState<SubscriptionTier | null>(null);
  const trackedPlanViewOrgId = useRef<string | null>(null);

  useEffect(() => {
    if (api) {
      fetchSubscriptionData();
    }
  }, [api]);

  useEffect(() => {
    if (
      loading ||
      !currentOrganization?.id ||
      trackedPlanViewOrgId.current === currentOrganization.id ||
      tiers.length === 0
    ) {
      return;
    }
    trackedPlanViewOrgId.current = currentOrganization.id;
    PostHogService.track(BillingEvent.PLAN_VIEWED, {
      organization_id: currentOrganization.id,
      organization_role: currentOrganization.current_user_role,
      organization_is_demo: currentOrganization.is_demo,
      access_type: accessType,
      current_subscription_tier: currentSubscription?.tier_name,
      has_current_subscription: Boolean(currentSubscription),
      tier_count: tiers.length,
      available_tiers: tiers.map((tier) => tier.name),
    });
  }, [loading, currentOrganization?.id, currentOrganization?.current_user_role, currentOrganization?.is_demo, accessType, currentSubscription, tiers]);

  const fetchSubscriptionData = async () => {
    try {
      const [tiersResponse, subscriptionResponse] = await Promise.all([
        api.get(apiEndpoints.billing.subscriptionTiers),
        api.get(apiEndpoints.billing.subscription)
      ]);

      setTiers(tiersResponse || []);
      setCurrentSubscription(subscriptionResponse);
      try {
        const quotaResponse = await api.get(apiEndpoints.billing.quotaBalance);
        setQuotaBalance(quotaResponse || null);
      } catch (quotaError) {
        setQuotaBalance(null);
      }
    } catch (err) {
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierName: string) => {
    const selectedTier = tiers.find((tier) => tier.name === tierName);
    PostHogService.track(BillingEvent.SUBSCRIPTION_CHECKOUT_CLICKED, {
      organization_id: currentOrganization?.id,
      organization_role: currentOrganization?.current_user_role,
      organization_is_demo: currentOrganization?.is_demo,
      access_type: accessType,
      tier_name: tierName,
      tier_price: selectedTier?.price,
      current_subscription_tier: currentSubscription?.tier_name,
    });

    setError(null);
    setPendingTier(tierName);
    try {
      const response = await api.post(apiEndpoints.billing.subscription, { tier_name: tierName });

      // Paddle in-place plan change (proration) — no checkout needed. The change is applied
      // asynchronously via a Paddle webhook, so the local mirror lags the charge by a moment.
      // Poll until the mirror reflects the new tier (or time out) so the UI doesn't appear
      // stuck on the old plan after the card has been charged.
      if (response.plan_change) {
        await pollForTierChange(tierName);
        await fetchSubscriptionData();
        return;
      }

      if (response.payment_required) {
        // CityPay redirects; for Paddle, the webhook creates the subscription a few seconds after payment.
        await startCheckout(api, response, {
          onComplete: async () => {
            setPendingTier(tierName);
            await pollForTierChange(tierName);
            await fetchSubscriptionData();
            setPendingTier(null);
          },
        });
      } else {
        await fetchSubscriptionData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create subscription');
    } finally {
      setPendingTier(null);
    }
  };

  // Poll the subscription endpoint until it reflects the target tier (webhook fulfilment is async).
  const pollForTierChange = async (tierName: string) => {
    for (let attempt = 0; attempt < 8; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      try {
        const sub = await api.get(apiEndpoints.billing.subscription);
        if (sub?.tier_name === tierName) return;
      } catch {
        // transient; keep polling
      }
    }
  };

  // A Paddle-managed subscription changes plan in-place (immediate proration on the saved card),
  // so we confirm before charging. A brand-new subscription (no active sub) goes through the
  // Paddle/CityPay checkout instead, which is its own confirmation step.
  const isInPlacePlanChange = (tier: SubscriptionTier) =>
    !!currentSubscription &&
    currentSubscription.provider === 'paddle' &&
    currentSubscription.tier_name !== tier.name;

  const handlePlanButtonClick = (tier: SubscriptionTier) => {
    if (isInPlacePlanChange(tier)) {
      setPlanChangeTier(tier);
    } else {
      handleSubscribe(tier.name);
    }
  };

  const confirmPlanChange = async () => {
    const tier = planChangeTier;
    setPlanChangeTier(null);
    if (tier) {
      await handleSubscribe(tier.name);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.delete(apiEndpoints.billing.subscription);
      await fetchSubscriptionData();
      setCancelDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleUncancel = async () => {
    setUncancelling(true);
    try {
      await api.post(apiEndpoints.billing.subscriptionUncancel);
      await fetchSubscriptionData();
    } catch (err: any) {
      setError(err.message || 'Failed to uncancel subscription');
    } finally {
      setUncancelling(false);
    }
  };

  const getTierIcon = (tierName: string) => {
    switch (tierName) {
      case 'creator':
      case 'starter':
        return { icon: Star, color: 'text-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-900/20' };
      case 'pro':
        return { icon: Zap, color: 'text-brand-primary', bgColor: 'bg-brand-primary/5 dark:bg-brand-primary/10' };
      default:
        return { icon: Star, color: 'text-gray-500', bgColor: 'bg-gray-50 dark:bg-gray-900/20' };
    }
  };

  const quotaTotals = quotaBalance
    ? {
        prs: quotaBalance.plan_prs + quotaBalance.topup_prs,
        sast: quotaBalance.plan_sast_scans + quotaBalance.topup_sast_scans,
        deep: quotaBalance.plan_deep_scans + quotaBalance.topup_deep_scans,
      }
    : null;
  const hasQuotaLimits = quotaTotals
    ? quotaTotals.prs > 0 || quotaTotals.sast > 0 || quotaTotals.deep > 0
    : false;
  const usageText = (remaining: number, total: number, plan: number) =>
    isUnlimitedQuota(plan) ? 'Unlimited' : `${remaining} / ${total}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size={32} />
      </div>
    );
  }

  const planChangeIsUpgrade =
    !!planChangeTier &&
    !!currentSubscription &&
    planChangeTier.price >= Number(currentSubscription.monthly_price);

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}
      
      {currentSubscription && (
        <div className="mb-8 p-6 bg-brand-primary/5 border border-brand-primary/20 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ui-text-primary">Current Subscription</h3>
            <div className="flex items-center gap-2">
              {currentSubscription.cancelled_at ? (
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                  Cancels {format(new Date(currentSubscription.current_period_end), 'MMM d, yyyy')}
                </span>
              ) : currentSubscription.payment_past_due ? (
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                  PAYMENT FAILED
                </span>
              ) : (
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  currentSubscription.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                }`}>
                  {currentSubscription.status.toUpperCase()}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <p className="text-sm text-ui-text-secondary mb-1">Plan</p>
                <p className="text-xl font-semibold text-ui-text-primary">
                  {tiers.find(t => t.name === currentSubscription.tier_name)?.display_name || currentSubscription.tier_name}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-ui-text-secondary mb-1">Monthly Price</p>
                <p className="text-xl font-semibold text-ui-text-primary">
                  ${currentSubscription.monthly_price}/month
                </p>
              </div>
            </div>
            
            <div>
              <div className="mb-4">
                <p className="text-sm text-ui-text-secondary mb-1">Current Period Ends</p>
                <p className="text-xl font-semibold text-ui-text-primary">
                  {format(new Date(currentSubscription.current_period_end), 'MMMM d, yyyy')}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-ui-text-secondary mb-1">
                  {currentSubscription.cancelled_at ? 'Subscription Ends' : 'Next Billing Date'}
                </p>
                <p className="text-xl font-semibold text-ui-text-primary">
                  {format(new Date(currentSubscription.cancelled_at ? currentSubscription.current_period_end : currentSubscription.next_billing_date), 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
          </div>
          
          {currentSubscription.cancelled_at && (
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Your subscription has been cancelled and will end on{' '}
                <strong>{format(new Date(currentSubscription.current_period_end), 'MMMM d, yyyy')}</strong>.
                You will continue to have access until this date.
              </p>
            </div>
          )}

          {quotaBalance && quotaTotals && hasQuotaLimits && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-ui-text-primary mb-3">Usage This Period</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-ui-background-secondary border border-ui-border-primary rounded-lg p-4">
                  <p className="text-xs text-ui-text-secondary">Fix PRs</p>
                  <p className="text-lg font-semibold text-ui-text-primary">
                    {usageText(quotaBalance.remaining_prs, quotaTotals.prs, quotaBalance.plan_prs)}
                  </p>
                  <p className="text-xs text-ui-text-tertiary">Used: {quotaBalance.used_prs}</p>
                </div>
                <div className="bg-ui-background-secondary border border-ui-border-primary rounded-lg p-4">
                  <p className="text-xs text-ui-text-secondary">SAST Scans</p>
                  <p className="text-lg font-semibold text-ui-text-primary">
                    {usageText(quotaBalance.remaining_sast_scans, quotaTotals.sast, quotaBalance.plan_sast_scans)}
                  </p>
                  <p className="text-xs text-ui-text-tertiary">Used: {quotaBalance.used_sast_scans}</p>
                </div>
                <div className="bg-ui-background-secondary border border-ui-border-primary rounded-lg p-4">
                  <p className="text-xs text-ui-text-secondary">Deep AI Scans</p>
                  <p className="text-lg font-semibold text-ui-text-primary">
                    {usageText(quotaBalance.remaining_deep_scans, quotaTotals.deep, quotaBalance.plan_deep_scans)}
                  </p>
                  <p className="text-xs text-ui-text-tertiary">Used: {quotaBalance.used_deep_scans}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            {!currentSubscription.cancelled_at && (
              <button
                onClick={() => setCancelDialogOpen(true)}
                className="px-4 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Cancel Subscription
              </button>
            )}
            {currentSubscription.cancelled_at && (
              <button
                onClick={handleUncancel}
                disabled={uncancelling}
                className="px-6 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50"
              >
                {uncancelling ? 'Processing...' : 'Reactivate Subscription'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Always show subscription plans */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const tierConfig = getTierIcon(tier.name);
            const Icon = tierConfig.icon;
            const isCurrentPlan = currentSubscription && currentSubscription.tier_name === tier.name;
            const blockedByCancellation = !isCurrentPlan && isInPlacePlanChange(tier) && !!currentSubscription?.cancelled_at;
            const quotaRows = [
              { label: 'PRs', value: tier.metadata?.quota_prs_per_month },
              { label: 'SAST Scans', value: tier.metadata?.quota_sast_scans_per_month },
              { label: 'Deep Scans', value: tier.metadata?.quota_deep_scans_per_month },
            ].filter((row) => row.value !== undefined && row.value !== null && row.value > 0);
            
            return (
              <div
                key={tier.id}
                className={`relative p-6 rounded-xl border transition-all duration-200 flex flex-col h-full ${
                  !tier.is_active
                    ? 'border-ui-border-primary bg-ui-background-tertiary opacity-75'
                    : isCurrentPlan
                    ? 'border-brand-primary bg-brand-primary/5 ring-2 ring-brand-primary/20'
                    : tier.name === 'pro'
                    ? 'border-brand-primary/50 hover:border-brand-primary'
                    : 'border-ui-border-primary bg-ui-background-secondary hover:border-brand-primary/50'
                }`}
              >
                {tier.name === 'pro' && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-brand-primary text-black px-3 py-1 text-xs font-medium rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-white px-3 py-1 text-xs font-medium rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}
                
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg ${tierConfig.bgColor} flex items-center justify-center`}>
                    <Icon size={20} className={tierConfig.color} />
                  </div>
                  <h4 className="font-bold text-ui-text-primary">{tier.display_name}</h4>
                </div>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    {tier.metadata?.requires_sales_contact || tier.price === 0 ? (
                      <>
                        <span className="text-3xl font-bold text-ui-text-primary">$Custom</span>
                        <span className="text-ui-text-secondary text-sm">/contact</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-ui-text-primary">${tier.price}</span>
                        <span className="text-ui-text-secondary text-sm">/month</span>
                      </>
                    )}
                  </div>
                  {tier.description && (
                    <p className="text-sm text-ui-text-secondary mt-2">{tier.description}</p>
                  )}
                </div>

                {quotaRows.length > 0 && (
                  <div className="mb-4 bg-ui-background-tertiary dark:bg-neutral-800/80 border border-ui-border-primary rounded-lg p-3">
                    <p className="text-xs font-semibold text-ui-text-secondary uppercase tracking-wide mb-2">
                      Monthly Limits
                    </p>
                    <div className="space-y-1 text-xs text-ui-text-secondary">
                      {quotaRows.map((row) => (
                        <div key={row.label} className="flex items-center justify-between">
                          <span>{row.label}</span>
                          <span className="font-medium text-ui-text-primary">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <ul className="space-y-2 mb-6 flex-grow">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-ui-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <button
                    onClick={() => {
                      if (!isCurrentPlan && !tier.metadata?.requires_sales_contact && tier.is_active && !pendingTier && !blockedByCancellation) {
                        handlePlanButtonClick(tier);
                      }
                    }}
                    disabled={tier.metadata?.requires_sales_contact || !!isCurrentPlan || !tier.is_active || !!pendingTier || blockedByCancellation}
                    title={blockedByCancellation ? 'Reactivate your subscription to change plans' : undefined}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      isCurrentPlan || blockedByCancellation
                        ? 'bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-tertiary cursor-not-allowed'
                        : !tier.is_active
                        ? 'bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-tertiary cursor-not-allowed'
                        : tier.metadata?.requires_sales_contact
                        ? 'bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-tertiary cursor-not-allowed'
                        : tier.name === 'pro'
                        ? 'bg-brand-primary text-black hover:bg-brand-primaryDark'
                        : 'bg-ui-background-tertiary dark:bg-neutral-600 text-ui-text-secondary border border-ui-border-primary hover:bg-ui-background-tertiary'
                    }`}
                  >
                    {pendingTier === tier.name ? 'Processing…' : isCurrentPlan ? 'Current Plan' : !tier.is_active ? 'Coming Soon' : tier.metadata?.requires_sales_contact ? 'Contact Sales' : 'Subscribe'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      {cancelDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-ui-background-secondary rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-ui-text-primary mb-4">
              Cancel Subscription
            </h3>
            <p className="text-ui-text-secondary mb-6">
              {currentSubscription?.payment_past_due
                ? "Your latest payment didn't go through, so cancelling ends your subscription now and stops any further payment attempts."
                : 'Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setCancelDialogOpen(false)}
                className="px-4 py-2 text-ui-text-secondary border border-ui-border-primary rounded-lg hover:bg-ui-background-tertiary transition-colors"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Change Confirmation Dialog (Paddle in-place proration charges the saved card immediately) */}
      {planChangeTier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-ui-background-secondary rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-ui-text-primary mb-4">
              {planChangeIsUpgrade ? 'Upgrade' : 'Change'} to {planChangeTier.display_name}?
            </h3>
            <p className="text-ui-text-secondary mb-2">
              Your plan changes to{' '}
              <strong>
                {planChangeTier.display_name} (${planChangeTier.price}/month)
              </strong>{' '}
              immediately.
            </p>
            <p className="text-ui-text-secondary mb-2">
              {planChangeIsUpgrade
                ? 'Your saved payment method will be charged a prorated amount for the rest of your current billing period now, then the full monthly price on your next billing date.'
                : 'A prorated credit for the remainder of your current billing period is applied to your account, and you’ll be billed the new monthly price from your next billing date.'}
            </p>
            <p className="text-ui-text-tertiary text-sm mb-6">
              Prices in USD. Taxes may apply and will be calculated at checkout.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPlanChangeTier(null)}
                className="px-4 py-2 text-ui-text-secondary border border-ui-border-primary rounded-lg hover:bg-ui-background-tertiary transition-colors"
              >
                Keep current plan
              </button>
              <button
                onClick={confirmPlanChange}
                className="px-4 py-2 bg-brand-primary text-black rounded-lg hover:bg-brand-primaryDark transition-colors"
              >
                Confirm change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 
